import { Component, inject, OnInit } from '@angular/core';
import { intersection } from 'lodash';
import { linkedQueryParam } from 'ngxtension/linked-query-param';
import { UserLoginService } from '../../service/user-login.service';
import { BranchRole, LocationRole, SystemRole } from '../../types';

type ViewMode =
  | 'branches'
  | 'userbranches'
  | 'userlocations'
  | 'users'
  | 'locations'
  | 'locationgroupswhitelist'
  | 'locationdomainwhitelist';

interface ViewModeConfig {
  name: string;
  mode: ViewMode;
  icon: string;
  tooltip: string;
  roles: (BranchRole | SystemRole | LocationRole)[];
}

@Component({
  selector: 'app-admin-user-management',
  templateUrl: './admin-user-management.component.html',
  styleUrl: './admin-user-management.component.scss',
  standalone: false,
})
export class AdminUserManagementComponent implements OnInit {
  private user = inject(UserLoginService);

  readonly viewMode = linkedQueryParam<ViewMode>('mode', {
    parse: (value) => (value ?? 'branches').toLowerCase() as ViewMode,
  });

  public readonly allViewModes: ViewModeConfig[] = [
    {
      name: 'Manage Branches',
      mode: 'branches',
      tooltip: 'Manage Branches',
      icon: 'account_tree',
      roles: ['admin', 'branch-owner', 'branch-manager'],
    },
    {
      name: 'Manage Locations',
      mode: 'locations',
      tooltip: 'Manage Locations',
      icon: 'lists',
      roles: ['admin', 'branch-owner', 'branch-manager', 'market-manager', 'location-manager'],
    },
    {
      name: "Manage Users' Assigned Branches",
      mode: 'userbranches',
      tooltip: "Manage Users' Assigned Branches",
      icon: 'recent_actors',
      roles: ['admin', 'branch-owner', 'branch-manager'],
    },
    {
      name: "Manage Users' Assigned Locations",
      mode: 'userlocations',
      tooltip: "Manage Users' Assigned Locations",
      icon: 'person_pin',
      roles: ['admin', 'branch-owner', 'branch-manager', 'market-manager'],
    },
    {
      name: 'Users',
      mode: 'users',
      tooltip: 'Manage all users',
      icon: 'group',
      roles: ['admin', 'branch-owner', 'branch-manager'],
    },
    {
      name: 'Manage Location Groups Domain Whitelist',
      mode: 'locationgroupswhitelist',
      tooltip: 'Manage Location Groups Domain Whitelist',
      icon: 'group_work',
      roles: ['admin'],
    },
    {
      name: 'Manage Domain Whitelisting',
      mode: 'locationdomainwhitelist',
      tooltip: 'Manage Domain Whitelisting',
      icon: 'night_shelter',
      roles: ['admin', 'branch-owner', 'branch-manager', 'market-manager'],
    },
  ];

  ngOnInit(): void {
    const mode = this.viewMode();
    const myRoles = this.user.currentProfileRoleList();
    const config = this.getViewModeConfig(mode);
    if (config && intersection(myRoles, config.roles).length > 0) {
      this.viewMode.set(mode as ViewMode);
    } else {
      const firstValidConfig = this.allViewModes.filter((vm) => {
        return intersection(myRoles, vm.roles).length > 0;
      })[0];

      if (firstValidConfig) {
        this.changeViewMode(firstValidConfig.mode);
      }
    }
  }

  public changeViewMode(viewMode: ViewMode): void {
    this.viewMode.set(viewMode);
  }

  private getViewModeConfig(mode: string | null) {
    return this.allViewModes.find((view) => view.mode === mode);
  }
}
